import isPlainObject from 'lodash.isplainobject';
import _regeneratorRuntime from 'babel-runtime/regenerator';
import isFunction from 'lodash.isfunction';

/**
 * String key that carries API call info interpreted by this Redux middleware.
 *
 * @constant {string}
 * @access public
 * @default
 */
var CALL_API = '@@redux-api-middleware/RSAA';

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};

var asyncToGenerator = function (fn) {
  return function () {
    var gen = fn.apply(this, arguments);
    return new Promise(function (resolve, reject) {
      function step(key, arg) {
        try {
          var info = gen[key](arg);
          var value = info.value;
        } catch (error) {
          reject(error);
          return;
        }

        if (info.done) {
          resolve(value);
        } else {
          return Promise.resolve(value).then(function (value) {
            step("next", value);
          }, function (err) {
            step("throw", err);
          });
        }
      }

      return step("next");
    });
  };
};

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();

var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

/**
 * Is the given action a plain JavaScript object with a [CALL_API] property?
 *
 * @function isRSAA
 * @access public
 * @param {object} action - The action to check
 * @returns {boolean}
 */
function isRSAA(action) {
  return isPlainObject(action) && action.hasOwnProperty(CALL_API);
}

/**
 * Is the given object a valid type descriptor?
 *
 * @function isValidTypeDescriptor
 * @access private
 * @param {object} obj - The object to check agains the type descriptor definition
 * @returns {boolean}
 */
function isValidTypeDescriptor(obj) {
  var validKeys = ['type', 'payload', 'meta'];

  if (!isPlainObject(obj)) {
    return false;
  }
  for (var key in obj) {
    if (!~validKeys.indexOf(key)) {
      return false;
    }
  }
  if (!('type' in obj)) {
    return false;
  } else if (typeof obj.type !== 'string' && _typeof(obj.type) !== 'symbol') {
    return false;
  }

  return true;
}

/**
 * Checks an action against the RSAA definition, returning a (possibly empty)
 * array of validation errors.
 *
 * @function validateRSAA
 * @access public
 * @param {object} action - The action to check against the RSAA definition
 * @returns {array}
 */
function validateRSAA(action) {
  var validationErrors = [];
  var validCallAPIKeys = ['endpoint', 'options', 'method', 'body', 'headers', 'credentials', 'bailout', 'types'];
  var validMethods = ['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'];
  var validCredentials = ['omit', 'same-origin', 'include'];

  if (!isRSAA(action)) {
    validationErrors.push('RSAAs must be plain JavaScript objects with a [CALL_API] property');
    return validationErrors;
  }

  for (var key in action) {
    if (key !== CALL_API) {
      validationErrors.push('Invalid root key: ' + key);
    }
  }

  var callAPI = action[CALL_API];
  if (!isPlainObject(callAPI)) {
    validationErrors.push('[CALL_API] property must be a plain JavaScript object');
  }
  for (var _key in callAPI) {
    if (!~validCallAPIKeys.indexOf(_key)) {
      validationErrors.push('Invalid [CALL_API] key: ' + _key);
    }
  }

  var endpoint = callAPI.endpoint,
      method = callAPI.method,
      headers = callAPI.headers,
      options = callAPI.options,
      credentials = callAPI.credentials,
      types = callAPI.types,
      bailout = callAPI.bailout;

  if (typeof endpoint === 'undefined') {
    validationErrors.push('[CALL_API] must have an endpoint property');
  } else if (typeof endpoint !== 'string' && typeof endpoint !== 'function') {
    validationErrors.push('[CALL_API].endpoint property must be a string or a function');
  }
  if (typeof method === 'undefined') {
    validationErrors.push('[CALL_API] must have a method property');
  } else if (typeof method !== 'string') {
    validationErrors.push('[CALL_API].method property must be a string');
  } else if (!~validMethods.indexOf(method.toUpperCase())) {
    validationErrors.push('Invalid [CALL_API].method: ' + method.toUpperCase());
  }

  if (typeof headers !== 'undefined' && !isPlainObject(headers) && typeof headers !== 'function') {
    validationErrors.push('[CALL_API].headers property must be undefined, a plain JavaScript object, or a function');
  }
  if (typeof options !== 'undefined' && !isPlainObject(options) && typeof options !== 'function') {
    validationErrors.push('[CALL_API].options property must be undefined, a plain JavaScript object, or a function');
  }
  if (typeof credentials !== 'undefined') {
    if (typeof credentials !== 'string') {
      validationErrors.push('[CALL_API].credentials property must be undefined, or a string');
    } else if (!~validCredentials.indexOf(credentials)) {
      validationErrors.push('Invalid [CALL_API].credentials: ' + credentials);
    }
  }
  if (typeof bailout !== 'undefined' && typeof bailout !== 'boolean' && typeof bailout !== 'function') {
    validationErrors.push('[CALL_API].bailout property must be undefined, a boolean, or a function');
  }

  if (typeof types === 'undefined') {
    validationErrors.push('[CALL_API] must have a types property');
  } else if (!Array.isArray(types) || types.length !== 3) {
    validationErrors.push('[CALL_API].types property must be an array of length 3');
  } else {
    var _types = slicedToArray(types, 3),
        requestType = _types[0],
        successType = _types[1],
        failureType = _types[2];

    if (typeof requestType !== 'string' && (typeof requestType === 'undefined' ? 'undefined' : _typeof(requestType)) !== 'symbol' && !isValidTypeDescriptor(requestType)) {
      validationErrors.push('Invalid request type');
    }
    if (typeof successType !== 'string' && (typeof successType === 'undefined' ? 'undefined' : _typeof(successType)) !== 'symbol' && !isValidTypeDescriptor(successType)) {
      validationErrors.push('Invalid success type');
    }
    if (typeof failureType !== 'string' && (typeof failureType === 'undefined' ? 'undefined' : _typeof(failureType)) !== 'symbol' && !isValidTypeDescriptor(failureType)) {
      validationErrors.push('Invalid failure type');
    }
  }

  return validationErrors;
}

/**
 * Is the given action a valid RSAA?
 *
 * @function isValidRSAA
 * @access public
 * @param {object} action - The action to check against the RSAA definition
 * @returns {boolean}
 */
function isValidRSAA(action) {
  return !validateRSAA(action).length;
}

/**
 * Error class for an RSAA that does not conform to the RSAA definition
 *
 * @class InvalidRSAA
 * @access public
 * @param {array} validationErrors - an array of validation errors
 */
var InvalidRSAA = function (_Error) {
  inherits(InvalidRSAA, _Error);

  function InvalidRSAA(validationErrors) {
    classCallCheck(this, InvalidRSAA);

    var _this = possibleConstructorReturn(this, (InvalidRSAA.__proto__ || Object.getPrototypeOf(InvalidRSAA)).call(this));

    _this.name = 'InvalidRSAA';
    _this.message = 'Invalid RSAA';
    _this.validationErrors = validationErrors;
    return _this;
  }

  return InvalidRSAA;
}(Error);

/**
 * Error class for a custom `payload` or `meta` function throwing
 *
 * @class InternalError
 * @access public
 * @param {string} message - the error message
 */


var InternalError = function (_Error2) {
  inherits(InternalError, _Error2);

  function InternalError(message) {
    classCallCheck(this, InternalError);

    var _this2 = possibleConstructorReturn(this, (InternalError.__proto__ || Object.getPrototypeOf(InternalError)).call(this));

    _this2.name = 'InternalError';
    _this2.message = message;
    return _this2;
  }

  return InternalError;
}(Error);

/**
 * Error class for an error raised trying to make an API call
 *
 * @class RequestError
 * @access public
 * @param {string} message - the error message
 */


var RequestError = function (_Error3) {
  inherits(RequestError, _Error3);

  function RequestError(message) {
    classCallCheck(this, RequestError);

    var _this3 = possibleConstructorReturn(this, (RequestError.__proto__ || Object.getPrototypeOf(RequestError)).call(this));

    _this3.name = 'RequestError';
    _this3.message = message;
    return _this3;
  }

  return RequestError;
}(Error);

/**
 * Error class for an API response outside the 200 range
 *
 * @class ApiError
 * @access public
 * @param {number} status - the status code of the API response
 * @param {string} statusText - the status text of the API response
 * @param {object} response - the parsed JSON response of the API server if the
 *  'Content-Type' header signals a JSON response
 */


var ApiError = function (_Error4) {
  inherits(ApiError, _Error4);

  function ApiError(status, statusText, response) {
    classCallCheck(this, ApiError);

    var _this4 = possibleConstructorReturn(this, (ApiError.__proto__ || Object.getPrototypeOf(ApiError)).call(this));

    _this4.name = 'ApiError';
    _this4.status = status;
    _this4.statusText = statusText;
    _this4.response = response;
    _this4.message = status + ' - ' + statusText;
    return _this4;
  }

  return ApiError;
}(Error);

/**
 * Extract JSON body from a server response
 *
 * @function getJSON
 * @access public
 * @param {object} res - A raw response object
 * @returns {promise|undefined}
 */
var getJSON = function () {
  var _ref = asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(res) {
    var contentType, emptyCodes;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            contentType = res.headers.get('Content-Type');
            emptyCodes = [204, 205];

            if (!(!~emptyCodes.indexOf(res.status) && contentType && ~contentType.indexOf('json'))) {
              _context.next = 8;
              break;
            }

            _context.next = 5;
            return res.json();

          case 5:
            return _context.abrupt('return', _context.sent);

          case 8:
            _context.next = 10;
            return Promise.resolve();

          case 10:
            return _context.abrupt('return', _context.sent);

          case 11:
          case 'end':
            return _context.stop();
        }
      }
    }, _callee, this);
  }));

  return function getJSON(_x) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * Blow up string or symbol types into full-fledged type descriptors,
 *   and add defaults
 *
 * @function normalizeTypeDescriptors
 * @access private
 * @param {array} types - The [CALL_API].types from a validated RSAA
 * @returns {array}
 */


/**
 * Evaluate a type descriptor to an FSA
 *
 * @function actionWith
 * @access private
 * @param {object} descriptor - A type descriptor
 * @param {array} args - The array of arguments for `payload` and `meta` function properties
 * @returns {object}
 */
var actionWith = function () {
  var _ref2 = asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(descriptor, args) {
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            _context2.next = 3;
            return typeof descriptor.payload === 'function' ? descriptor.payload.apply(descriptor, toConsumableArray(args)) : descriptor.payload;

          case 3:
            descriptor.payload = _context2.sent;
            _context2.next = 10;
            break;

          case 6:
            _context2.prev = 6;
            _context2.t0 = _context2['catch'](0);

            descriptor.payload = new InternalError(_context2.t0.message);
            descriptor.error = true;

          case 10:
            _context2.prev = 10;
            _context2.next = 13;
            return typeof descriptor.meta === 'function' ? descriptor.meta.apply(descriptor, toConsumableArray(args)) : descriptor.meta;

          case 13:
            descriptor.meta = _context2.sent;
            _context2.next = 21;
            break;

          case 16:
            _context2.prev = 16;
            _context2.t1 = _context2['catch'](10);

            delete descriptor.meta;
            descriptor.payload = new InternalError(_context2.t1.message);
            descriptor.error = true;

          case 21:
            return _context2.abrupt('return', descriptor);

          case 22:
          case 'end':
            return _context2.stop();
        }
      }
    }, _callee2, this, [[0, 6], [10, 16]]);
  }));

  return function actionWith(_x2, _x3) {
    return _ref2.apply(this, arguments);
  };
}();
function normalizeTypeDescriptors(types) {
  var _types = slicedToArray(types, 3),
      requestType = _types[0],
      successType = _types[1],
      failureType = _types[2];

  if (typeof requestType === 'string' || (typeof requestType === 'undefined' ? 'undefined' : _typeof(requestType)) === 'symbol') {
    requestType = { type: requestType };
  }

  if (typeof successType === 'string' || (typeof successType === 'undefined' ? 'undefined' : _typeof(successType)) === 'symbol') {
    successType = { type: successType };
  }
  successType = _extends({
    payload: function payload(action, state, res) {
      return getJSON(res);
    }
  }, successType);

  if (typeof failureType === 'string' || (typeof failureType === 'undefined' ? 'undefined' : _typeof(failureType)) === 'symbol') {
    failureType = { type: failureType };
  }
  failureType = _extends({
    payload: function payload(action, state, res) {
      return getJSON(res).then(function (json) {
        return new ApiError(res.status, res.statusText, json);
      });
    }
  }, failureType);

  return [requestType, successType, failureType];
}

/**
 * A Redux middleware that processes RSAA actions.
 *
 * @type {ReduxMiddleware}
 * @access public
 */
var providedFetch = void 0;
function apiMiddleware(storeOrFetch) {
  var _this = this;

  if (isFunction(storeOrFetch)) {
    providedFetch = storeOrFetch;
    return apiMiddleware;
  }
  var getState = storeOrFetch.getState;


  return function (next) {
    return function (action) {
      // Do not process actions without a [CALL_API] property
      if (!isRSAA(action)) {
        return next(action);
      }

      return asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var validationErrors, _callAPI, _requestType, callAPI, endpoint, body, headers, _callAPI$options, options, method, credentials, bailout, types, _normalizeTypeDescrip, _normalizeTypeDescrip2, requestType, successType, failureType, res;

        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                // Try to dispatch an error request FSA for invalid RSAAs
                validationErrors = validateRSAA(action);

                if (!validationErrors.length) {
                  _context.next = 5;
                  break;
                }

                _callAPI = action[CALL_API];

                if (_callAPI.types && Array.isArray(_callAPI.types)) {
                  _requestType = _callAPI.types[0];

                  if (_requestType && _requestType.type) {
                    _requestType = _requestType.type;
                  }
                  next({
                    type: _requestType,
                    payload: new InvalidRSAA(validationErrors),
                    error: true
                  });
                }
                return _context.abrupt('return');

              case 5:

                // Parse the validated RSAA action
                callAPI = action[CALL_API];
                endpoint = callAPI.endpoint, body = callAPI.body, headers = callAPI.headers, _callAPI$options = callAPI.options, options = _callAPI$options === undefined ? {} : _callAPI$options;
                method = callAPI.method, credentials = callAPI.credentials, bailout = callAPI.bailout, types = callAPI.types;
                _normalizeTypeDescrip = normalizeTypeDescriptors(types), _normalizeTypeDescrip2 = slicedToArray(_normalizeTypeDescrip, 3), requestType = _normalizeTypeDescrip2[0], successType = _normalizeTypeDescrip2[1], failureType = _normalizeTypeDescrip2[2];

                // Should we bail out?

                _context.prev = 9;

                if (!(typeof bailout === 'boolean' && bailout || typeof bailout === 'function' && bailout(getState()))) {
                  _context.next = 12;
                  break;
                }

                return _context.abrupt('return');

              case 12:
                _context.next = 21;
                break;

              case 14:
                _context.prev = 14;
                _context.t0 = _context['catch'](9);
                _context.t1 = next;
                _context.next = 19;
                return actionWith(_extends({}, requestType, {
                  payload: new RequestError('[CALL_API].bailout function failed'),
                  error: true
                }), [action, getState()]);

              case 19:
                _context.t2 = _context.sent;
                return _context.abrupt('return', (0, _context.t1)(_context.t2));

              case 21:
                if (!(typeof endpoint === 'function')) {
                  _context.next = 33;
                  break;
                }

                _context.prev = 22;

                endpoint = endpoint(getState());
                _context.next = 33;
                break;

              case 26:
                _context.prev = 26;
                _context.t3 = _context['catch'](22);
                _context.t4 = next;
                _context.next = 31;
                return actionWith(_extends({}, requestType, {
                  payload: new RequestError('[CALL_API].endpoint function failed'),
                  error: true
                }), [action, getState()]);

              case 31:
                _context.t5 = _context.sent;
                return _context.abrupt('return', (0, _context.t4)(_context.t5));

              case 33:
                if (!(typeof body === 'function')) {
                  _context.next = 45;
                  break;
                }

                _context.prev = 34;

                body = body(getState());
                _context.next = 45;
                break;

              case 38:
                _context.prev = 38;
                _context.t6 = _context['catch'](34);
                _context.t7 = next;
                _context.next = 43;
                return actionWith(_extends({}, requestType, {
                  payload: new RequestError('[CALL_API].body function failed'),
                  error: true
                }), [action, getState()]);

              case 43:
                _context.t8 = _context.sent;
                return _context.abrupt('return', (0, _context.t7)(_context.t8));

              case 45:
                if (!(typeof headers === 'function')) {
                  _context.next = 57;
                  break;
                }

                _context.prev = 46;

                headers = headers(getState());
                _context.next = 57;
                break;

              case 50:
                _context.prev = 50;
                _context.t9 = _context['catch'](46);
                _context.t10 = next;
                _context.next = 55;
                return actionWith(_extends({}, requestType, {
                  payload: new RequestError('[CALL_API].headers function failed'),
                  error: true
                }), [action, getState()]);

              case 55:
                _context.t11 = _context.sent;
                return _context.abrupt('return', (0, _context.t10)(_context.t11));

              case 57:
                if (!(typeof options === 'function')) {
                  _context.next = 69;
                  break;
                }

                _context.prev = 58;

                options = options(getState());
                _context.next = 69;
                break;

              case 62:
                _context.prev = 62;
                _context.t12 = _context['catch'](58);
                _context.t13 = next;
                _context.next = 67;
                return actionWith(_extends({}, requestType, {
                  payload: new RequestError('[CALL_API].options function failed'),
                  error: true
                }), [action, getState()]);

              case 67:
                _context.t14 = _context.sent;
                return _context.abrupt('return', (0, _context.t13)(_context.t14));

              case 69:
                if (!(typeof requestType.payload === 'function' || typeof requestType.meta === 'function')) {
                  _context.next = 77;
                  break;
                }

                _context.t15 = next;
                _context.next = 73;
                return actionWith(requestType, [action, getState()]);

              case 73:
                _context.t16 = _context.sent;
                (0, _context.t15)(_context.t16);
                _context.next = 78;
                break;

              case 77:
                next(requestType);

              case 78:
                _context.prev = 78;
                _context.next = 81;
                return (providedFetch || fetch)(endpoint, _extends({}, options, {
                  method: method, body: body, credentials: credentials, headers: headers
                }));

              case 81:
                res = _context.sent;
                _context.next = 91;
                break;

              case 84:
                _context.prev = 84;
                _context.t17 = _context['catch'](78);
                _context.t18 = next;
                _context.next = 89;
                return actionWith(_extends({}, requestType, {
                  payload: new RequestError(_context.t17.message),
                  error: true
                }), [action, getState()]);

              case 89:
                _context.t19 = _context.sent;
                return _context.abrupt('return', (0, _context.t18)(_context.t19));

              case 91:
                if (!res.ok) {
                  _context.next = 99;
                  break;
                }

                _context.t20 = next;
                _context.next = 95;
                return actionWith(successType, [action, getState(), res]);

              case 95:
                _context.t21 = _context.sent;
                return _context.abrupt('return', (0, _context.t20)(_context.t21));

              case 99:
                _context.t22 = next;
                _context.next = 102;
                return actionWith(_extends({}, failureType, {
                  error: true
                }), [action, getState(), res]);

              case 102:
                _context.t23 = _context.sent;
                return _context.abrupt('return', (0, _context.t22)(_context.t23));

              case 104:
              case 'end':
                return _context.stop();
            }
          }
        }, _callee, _this, [[9, 14], [22, 26], [34, 38], [46, 50], [58, 62], [78, 84]]);
      }))();
    };
  };
}

/**
 * Redux middleware for calling an API
 * @module redux-api-middleware
 * @requires isomorphic-fetch
 * @requires lodash.isplainobject
 * @exports {string} CALL_API
 * @exports {function} isRSAA
 * @exports {function} validateRSAA
 * @exports {function} isValidRSAA
 * @exports {error} InvalidRSAA
 * @exports {error} InternalError
 * @exports {error} RequestError
 * @exports {error} ApiError
 * @exports {function} getJSON
 * @exports {ReduxMiddleWare} apiMiddleware
 */

export { CALL_API, isRSAA, validateRSAA, isValidRSAA, InvalidRSAA, InternalError, RequestError, ApiError, getJSON, apiMiddleware };



//////////////////
// WEBPACK FOOTER
// ./~/veritone-redux-common/~/redux-api-middleware-fixed/lib/bundle-es.js
// module id = 0XoW
// module chunks = 22